import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CreditCard, Coins, RefreshCcw, AlertTriangle, ArrowRight, Download } from "lucide-react";

const money = (cents) => `$${((cents ?? 0)/100).toFixed(2)}`;

export default function Credits() {
  const [state, setState] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);

  const load = async () => {
    const [c, l] = await Promise.all([api.get("/credits"), api.get("/credits/ledger")]);
    setState(c.data); setLedger(l.data);
  };

  useEffect(() => { load().catch(()=>{}); }, []);

  // handle top-up return
  useEffect(() => {
    const t = params.get("topup");
    const sid = params.get("session_id");
    if (t === "cancel") { toast.info("Top-up cancelled"); setParams({}); return; }
    if (t === "success" && sid && !polling) {
      setPolling(true);
      let attempts = 0;
      const iv = setInterval(async () => {
        attempts++;
        try {
          const r = await api.get(`/credits/topup/${sid}`);
          if (r.data.payment_status === "paid") {
            clearInterval(iv); setPolling(false); setParams({});
            toast.success("Credits added");
            load();
            const back = localStorage.getItem("iat_return_to_mail");
            if (back) { localStorage.removeItem("iat_return_to_mail"); nav(back); }
          } else if (attempts >= 12) {
            clearInterval(iv); setPolling(false);
            toast.warning("Payment not confirmed yet — refresh in a moment");
          }
        } catch { clearInterval(iv); setPolling(false); }
      }, 2000);
    }
  // eslint-disable-next-line
  }, [params]);

  const topup = async (cents) => {
    setBusy(true);
    try {
      const r = await api.post("/credits/topup", { amount_cents: cents, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Top-up failed"); setBusy(false); }
  };

  const saveAuto = async (patch) => {
    const next = { ...(state?.auto_topup||{}), ...patch };
    await api.put("/credits/auto-topup", next);
    toast.success("Auto top-up saved"); load();
  };

  const low = state && state.balance_cents < state.low_balance_threshold_cents;

  return (
    <AppShell>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Accelerator Mail Credits</div>
          <h1 className="text-3xl font-semibold text-slate-900 mt-1">Mail Credits</h1>
          <p className="text-slate-500 text-sm mt-1">Prepaid credits used exclusively to mail dispute letters through the app.</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="glass rounded-2xl lg:col-span-1">
          <CardContent className="p-6">
            <div className="text-xs text-purple-700 uppercase tracking-widest">Current balance</div>
            <div className="text-4xl font-semibold text-slate-900 mt-2" data-testid="credits-balance">{money(state?.balance_cents)}</div>
            {low && (
              <div className="mt-3 flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 text-xs" data-testid="credits-low-banner">
                <AlertTriangle size={14}/> Low balance — top up before mailing your next letter.
              </div>
            )}
            <div className="mt-4 flex items-center gap-2">
              <Coins size={14} className="text-purple-600"/>
              <span className="text-xs text-slate-500">Credits are non-transferable and non-refundable outside of Lob rejections.</span>
            </div>
          </CardContent>
        </Card>

        <Card className="glass rounded-2xl lg:col-span-2">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-3"><CreditCard size={16} className="text-purple-600"/><div className="text-slate-900 font-semibold">Add Mail Credits</div></div>
            <p className="text-xs text-slate-500 mb-4">Choose a top-up amount. Payment is processed by Stripe. {state?.live_topups_enabled ? "" : <span className="text-amber-700" data-testid="credits-topup-unavailable">Mail-credit purchases are temporarily unavailable during launch preparation.</span>}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(state?.topup_amounts_cents || []).map(c => (
                <Button key={c} onClick={()=>topup(c)} disabled={busy} className="btn-primary rounded-xl h-14 text-base" data-testid={`credits-topup-${c}`}>Add {money(c)}</Button>
              ))}
            </div>
            <div className="mt-5 rounded-xl border border-slate-200 p-4 bg-slate-50">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm text-slate-900 font-medium">Auto top-up</div>
                  <div className="text-xs text-slate-500">Automatically buy more credits when your balance drops below the threshold.</div>
                </div>
                <Switch checked={!!state?.auto_topup?.enabled} onCheckedChange={(v)=>saveAuto({ enabled: v })} data-testid="credits-auto-toggle"/>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="glass rounded-2xl mt-6">
        <CardContent className="p-0 overflow-x-auto">
          <div className="p-4 flex items-center justify-between">
            <div className="text-slate-900 font-semibold">Transaction history</div>
            <Button variant="ghost" size="sm" onClick={load} className="text-slate-600"><RefreshCcw size={13} className="mr-1"/>Refresh</Button>
          </div>
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
              <tr><th className="p-3">When</th><th className="p-3">Type</th><th className="p-3 text-right">Amount</th><th className="p-3 text-right">Balance after</th><th className="p-3">Reference</th><th className="p-3">Receipt</th></tr>
            </thead>
            <tbody data-testid="credits-ledger">
              {ledger.map(r => (
                <tr key={r.id} className="border-b border-slate-200">
                  <td className="p-3 text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="p-3"><span className="stage-pill rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest">{r.type}</span></td>
                  <td className={`p-3 text-right font-mono ${r.amount_cents>=0?"text-emerald-700":"text-red-700"}`}>{r.amount_cents>=0?"+":""}{money(r.amount_cents)}</td>
                  <td className="p-3 text-right text-slate-900">{money(r.balance_after_cents)}</td>
                  <td className="p-3 text-xs text-slate-500 font-mono">{r.stripe_session_id || r.lob_mailing_id || r.reason || "—"}</td>
                  <td className="p-3">
                    {r.type === "topup" && r.stripe_session_id && (
                      <a href={`https://dashboard.stripe.com/test/payments/${r.stripe_payment_intent_id || ""}`} target="_blank" rel="noreferrer" className="text-purple-700 text-xs inline-flex items-center gap-1"><Download size={12}/>Receipt</a>
                    )}
                  </td>
                </tr>
              ))}
              {ledger.length===0 && <tr><td colSpan="6" className="p-6 text-slate-500 text-center">No transactions yet.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </AppShell>
  );
}
